'use client'

/**
 * Primitivos VISUAIS do bloco de Listagem Comparativa (genérico).
 *
 * Porta agnóstica de `editorial-review-primitives.tsx` do protótipo. Todo texto vem por prop
 * (nada de domínio cravado). Estilo FIEL ao plano §6 (classes Tailwind exatas).
 *
 * SEO obrigatório:
 *  - SEO-01: `DetailsReveal` mantém os `children` SEMPRE no DOM, apenas colapsados por CSS.
 *  - SEO-04: `OfferActions` aplica `rel`/`target` recebidos por prop no `<a>` do CTA.
 */

import {
  ArrowRight,
  BadgeCheck,
  Check,
  ChevronDown,
  CircleHelp,
  Star,
  X,
} from 'lucide-react'
import type { ReactNode } from 'react'

import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/utilities/ui'

import { formatScore } from '@/blocks/ComparisonListing/lib'
import { useNumberLocale } from '@/modules/languages/lib/useNumberLocale'
import { useDetailsReveal } from './use-details-reveal'

// ── Estilos de tabela compartilhados (FIEL ao plano §6) ──────────────────────
export const listingTableStyles = {
  // Header: no LIGHT escuro (bg-primary, ~20% = quase-preto, texto claro); no DARK suave (bg-muted/60,
  // a mesma cor do campo de busca). Pedido do dono.
  headerCell:
    'h-11 bg-primary text-primary-foreground dark:bg-muted/60 dark:text-foreground px-2 text-left text-xs font-semibold tracking-tight align-middle',
  bodyCell: 'px-2 py-4 align-middle',
  bodyCellContent: 'flex min-h-[4.5rem] flex-col justify-center gap-0.5',
  metricValue:
    'block text-xs font-semibold leading-tight tabular-nums text-foreground sm:text-sm',
  metricNote: 'block min-h-[1rem] text-[11px] leading-tight text-muted-foreground',
  detailList:
    'list-disc space-y-1 pl-5 text-sm leading-snug text-muted-foreground marker:text-muted-foreground',
} as const

// ── InfoTip (tooltip de ajuda «?») ───────────────────────────────────────────
export function InfoTip({
  label,
  text,
  variant = 'default',
}: {
  label: string
  text?: string | null
  /** `onHeader` = ícone adapta ao tema (light: claro p/ header escuro; dark: foreground p/ header muted). */
  variant?: 'default' | 'onPrimary' | 'onHeader'
}) {
  if (!text) return null

  // Header escuro no light + muted no dark → o ícone segue: claro no light, foreground no dark.
  const onDark = variant === 'onPrimary' || variant === 'onHeader'
  const iconClass =
    variant === 'onHeader'
      ? 'text-primary-foreground/75 hover:bg-primary-foreground/10 hover:text-primary-foreground dark:text-foreground/70 dark:hover:bg-foreground/10 dark:hover:text-foreground'
      : variant === 'onPrimary'
        ? 'text-primary-foreground/75 hover:bg-primary-foreground/10 hover:text-primary-foreground'
        : 'text-primary hover:text-primary/80'

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className={cn('inline-flex shrink-0 rounded-sm transition-colors', iconClass)}
          aria-label={label}
        >
          <CircleHelp className="size-3.5" aria-hidden />
        </button>
      </TooltipTrigger>
      <TooltipContent
        className={cn(
          'max-w-[14rem] text-xs leading-snug',
          onDark && 'border border-border bg-popover text-popover-foreground shadow-md',
        )}
        side={onDark ? 'bottom' : undefined}
        sideOffset={onDark ? 8 : 4}
      >
        {text}
      </TooltipContent>
    </Tooltip>
  )
}

// ── StarScore (nota + estrela) ───────────────────────────────────────────────
export function StarScore({
  score,
  max = 5,
  compact,
  className,
}: {
  score: number
  max?: number
  compact?: boolean
  className?: string
}) {
  const numberLocale = useNumberLocale()
  const formatted = formatScore(score, numberLocale)
  const label = `Pontuação ${formatted} de ${max}`

  return (
    <span
      className={cn('inline-flex items-center gap-1.5', className)}
      role="img"
      aria-label={label}
    >
      <span
        className={cn('font-semibold tabular-nums', compact ? 'text-xs' : 'text-xs sm:text-sm')}
        aria-hidden
      >
        {formatted}
      </span>
      {!compact ? (
        <span className="text-xs font-semibold text-muted-foreground" aria-hidden>
          /{max}
        </span>
      ) : null}
      <Star className="size-3.5 fill-amber-400 text-amber-400" aria-hidden />
    </span>
  )
}

// ── VerifiedBadge (selo «verificado») ────────────────────────────────────────
export function VerifiedBadge({
  compact,
  ariaLabel,
  label,
  tooltip,
  className,
}: {
  compact?: boolean
  ariaLabel: string
  label: string
  tooltip: string
  className?: string
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex max-w-full items-center gap-1 text-left text-muted-foreground',
            compact ? 'text-[10px]' : 'mt-2.5 text-[10px] sm:text-[11px]',
            className,
          )}
          aria-label={ariaLabel}
        >
          <BadgeCheck
            className={cn(
              'shrink-0 text-green-600 dark:text-emerald-400',
              compact ? 'size-3' : 'size-3.5',
            )}
            aria-hidden
          />
          <span
            className={cn(
              'inline-flex items-center leading-none',
              compact ? 'min-h-3' : 'min-h-3.5',
            )}
          >
            {label}
          </span>
        </button>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  )
}

// ── LogoLink (logo OPCIONAL: sem src mostra o NOME do operador) ───────────────
export function LogoLink({
  href,
  label,
  name,
  src,
  alt,
  width = 120,
  height = 45,
  compact,
  eager,
  logoBackground,
}: {
  href?: string | null
  label: string
  name: string
  src?: string | null
  alt: string
  width?: number
  height?: number
  compact?: boolean
  eager?: boolean
  logoBackground?: string | null
}) {
  const boxClass = cn(
    'inline-flex items-center justify-center rounded-sm border border-border/50 bg-background',
    compact ? 'h-9 w-[5.5rem]' : 'h-11 w-[6.75rem]',
  )
  // Fundo custom do logo (opcional): sobrescreve `bg-background` e dá um respiro (padding). Vazio = padrão.
  const boxStyle =
    typeof logoBackground === 'string' && logoBackground.trim()
      ? { backgroundColor: logoBackground.trim(), padding: '5px' }
      : undefined

  const inner = src ? (
    // Bandeiras/logos via <img> (flagcdn/blob); NÃO next/image (contrato §6).
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={src}
      alt={alt}
      width={width}
      height={height}
      loading={eager ? 'eager' : 'lazy'}
      fetchPriority={eager ? 'high' : undefined}
      decoding="async"
      className={cn('max-h-full max-w-full object-contain', compact ? 'max-h-7' : 'max-h-9')}
    />
  ) : (
    // Sem logo: cai no NOME do operador numa caixa.
    <span
      className={cn(
        'line-clamp-2 px-1.5 text-center font-semibold leading-tight text-foreground',
        compact ? 'text-[11px]' : 'text-xs sm:text-sm',
      )}
    >
      {name}
    </span>
  )

  if (!href) {
    return (
      <span className={boxClass} style={boxStyle} aria-label={label}>
        {inner}
      </span>
    )
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className={boxClass}
      style={boxStyle}
    >
      {inner}
    </a>
  )
}

// ── RankBadge (#N do ranking; #1 destacado) ──────────────────────────────────
export function RankBadge({ rank, compact }: { rank: number; compact?: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md px-1.5 py-0.5 font-bold tabular-nums leading-none',
        compact ? 'text-[10px]' : 'text-[11px]',
        rank === 1
          ? 'bg-primary text-primary-foreground shadow-sm'
          : 'bg-muted text-muted-foreground',
      )}
      aria-label={`Posição ${rank} no ranking`}
    >
      #{rank}
    </span>
  )
}

// ── OfferActions (CTA afiliado + toggle de detalhes) ─────────────────────────
export function OfferActions({
  ctaHref,
  ctaLabel,
  ctaRel,
  ctaOpenNewTab,
  detailsId,
  open,
  onToggle,
  showMoreLabel,
  showLessLabel,
  compact,
  enableDetails = true,
  containerClassName,
}: {
  ctaHref: string
  ctaLabel: string
  ctaRel: string
  ctaOpenNewTab: boolean
  detailsId: string
  open: boolean
  onToggle: () => void
  showMoreLabel: string
  showLessLabel: string
  compact?: boolean
  enableDetails?: boolean
  containerClassName?: string
}) {
  return (
    <div
      className={cn(
        'flex w-full max-w-[11.75rem] shrink-0 flex-col items-stretch',
        compact ? 'max-w-[10.5rem] gap-2' : 'gap-3',
        containerClassName,
      )}
    >
      <Button
        className={cn('w-full whitespace-nowrap', compact ? 'h-8 text-xs' : 'h-9 text-sm')}
        asChild
      >
        {/* SEO-04: rel/target vêm do config (rel pode ser ''→sem atributo). */}
        <a
          href={ctaHref}
          target={ctaOpenNewTab ? '_blank' : undefined}
          rel={ctaRel || undefined}
        >
          {ctaLabel}
          <ArrowRight className={compact ? 'size-3' : 'size-3.5'} aria-hidden />
        </a>
      </Button>
      {enableDetails ? (
        <button
          type="button"
          className={cn(
            'flex w-full items-center justify-center gap-0.5 text-center text-primary transition-colors hover:text-primary/80',
            compact ? 'text-[10px] leading-tight' : 'text-[11px] sm:text-xs',
          )}
          aria-expanded={open}
          aria-controls={detailsId}
          onClick={onToggle}
        >
          <span className="min-w-0">{open ? showLessLabel : showMoreLabel}</span>
          <ChevronDown
            className={cn(
              'shrink-0 transition-transform duration-300',
              compact ? 'size-3' : 'size-3.5',
              open && 'rotate-180',
            )}
            aria-hidden
          />
        </button>
      ) : null}
    </div>
  )
}

// ── MobileMetrics (lista de métricas em <dl> no card mobile) ──────────────────
export type ListingMobileMetric = {
  label: string
  value: ReactNode
  note?: string | null
  tooltip?: string | null
}

export function MobileMetrics({ metrics }: { metrics: ListingMobileMetric[] }) {
  return (
    <dl className="space-y-0 px-2.5 pb-2">
      {metrics.map((metric) => (
        <div
          key={metric.label}
          className="flex items-center justify-between gap-2 border-t border-border py-1.5 first:border-t-0"
        >
          <dt className="flex min-w-0 items-center gap-1 text-[11px] leading-tight text-muted-foreground">
            <span className="line-clamp-2">{metric.label}</span>
            <InfoTip label={metric.tooltip ?? metric.label} text={metric.tooltip} />
          </dt>
          <dd className="flex shrink-0 flex-col items-end justify-center text-end">
            <data
              value={String(metric.value)}
              className="text-sm font-semibold leading-tight tabular-nums text-foreground"
            >
              {metric.value}
            </data>
            {metric.note ? (
              <span className="text-[10px] leading-tight text-muted-foreground">{metric.note}</span>
            ) : null}
          </dd>
        </div>
      ))}
    </dl>
  )
}

// ── TableMetricCell (célula de atributo na tabela desktop) ────────────────────
export function TableMetricCell({
  value,
  note,
  align = 'left',
}: {
  value: ReactNode
  note?: string | null
  align?: 'left' | 'center' | 'right'
}) {
  const alignClass =
    align === 'center' ? 'items-center text-center' : align === 'right' ? 'items-end text-right' : ''

  return (
    <td className={cn(listingTableStyles.bodyCell, align !== 'left' && 'text-center')}>
      <div className={cn(listingTableStyles.bodyCellContent, alignClass)}>
        <data value={String(value)} className={listingTableStyles.metricValue}>
          {value}
        </data>
        <span className={cn(listingTableStyles.metricNote, !note && 'invisible')}>
          {note ?? ' '}
        </span>
      </div>
    </td>
  )
}

// ── ExpandedPanel (conteúdo do painel: parecer + prós + contras) ─────────────
function DetailListSection({
  id,
  title,
  icon: Icon,
  iconClassName,
  items,
}: {
  id: string
  title: string
  icon: typeof Check
  iconClassName: string
  items: string[]
}) {
  if (!items.length) return null
  return (
    <section aria-labelledby={id}>
      <header className="mb-3 flex items-center gap-2">
        <Icon className={cn('size-5', iconClassName)} aria-hidden />
        <h4 id={id} className="text-base font-semibold">
          {title}
        </h4>
      </header>
      <ul className={listingTableStyles.detailList}>
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </section>
  )
}

export function ExpandedPanel({
  itemId,
  heading,
  expertTake,
  prosHeading,
  pros,
  consHeading,
  cons,
  panelLabel,
}: {
  itemId: string
  heading: string
  expertTake?: string | null
  prosHeading: string
  pros: string[]
  consHeading: string
  cons: string[]
  panelLabel?: string
}) {
  const expertParagraphs = (expertTake ?? '')
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean)

  return (
    <aside className="bg-muted/40 px-4 pt-4 pb-6" aria-label={panelLabel ?? heading}>
      {expertParagraphs.length ? (
        <article className="flex flex-col gap-4 rounded-lg border border-border bg-card p-4 text-card-foreground shadow-sm">
          <header className="flex items-center gap-3">
            <h3 className="text-xs font-bold tracking-wider text-foreground uppercase">{heading}</h3>
          </header>
          <div className="flex flex-col gap-2 text-sm leading-relaxed text-foreground">
            {expertParagraphs.map((para, index) => (
              <p key={index}>{para}</p>
            ))}
          </div>
        </article>
      ) : null}

      {pros.length || cons.length ? (
        <div className="mt-6 grid gap-6 pl-4 md:grid-cols-2 sm:pl-6">
          <DetailListSection
            id={`pros-${itemId}`}
            title={prosHeading}
            icon={Check}
            iconClassName="text-green-500"
            items={pros}
          />
          <DetailListSection
            id={`cons-${itemId}`}
            title={consHeading}
            icon={X}
            iconClassName="text-destructive"
            items={cons}
          />
        </div>
      ) : null}
    </aside>
  )
}

// ── DetailsReveal (SEO-01: children SEMPRE no DOM, colapsados por CSS) ────────
export function DetailsReveal({
  open,
  detailsId,
  contentKey,
  children,
}: {
  open: boolean
  detailsId: string
  contentKey: string
  children: ReactNode
}) {
  const { innerRef, panelHeight } = useDetailsReveal(open, contentKey)

  return (
    <div
      id={detailsId}
      className={cn(
        'overflow-hidden border-border transition-[max-height] duration-300 ease-in-out motion-reduce:transition-none',
        open && 'border-t',
      )}
      style={{ maxHeight: open ? panelHeight : 0 }}
      aria-hidden={!open}
    >
      {/* children NUNCA são desmontados (visíveis aos crawlers mesmo fechado). */}
      <div ref={innerRef}>{children}</div>
    </div>
  )
}
